/**
 * The gas you cross inside.
 *
 * ── Two effects, and they have now genuinely parted ─────────────────────────
 *
 * Streaks and gas were once a single shader, which was wrong because they answer
 * different questions and appear at different times. They were split into two
 * layers on one canvas, which was better and still not far enough: **star
 * streaks** say *you are moving very fast* and belong to any warp run, while
 * **nebulae** say *you are not in a system any more*, which only a crossing can
 * mean.
 *
 * The streaks have left entirely. They are scene geometry now — see
 * `warpStreaks` — because a star being passed has to be occluded by the hull
 * passing it, and a canvas over the render can never do that at any stacking
 * order. What is left here is the gas.
 *
 * ── Why the gas keeps its own renderer ──────────────────────────────────────
 *
 * The flight scene is *destroyed* mid-crossing when the system is swapped, and
 * this is what covers that. Anything drawn in the flight scene would go with it.
 * So it stays a small renderer of its own that outlives the swap — mounted
 * *below* the flight canvas, which is transparent, so the ship still draws in
 * front of the cloud it is flying through.
 *
 * Gas is continuous noise rather than particles because gas has no edges: drawn
 * as discrete blobs it popped into existence at its own boundaries and never
 * overlapped into anything cloudlike.
 */

import { useEffect, useRef } from 'react';
import * as THREE from 'three';

/** Seconds for the crossing's own contribution to wind in, and to let go. */
const CROSSING_IN = 2.2;

/**
 * Faster out than in, and deliberately so.
 *
 * Arriving, the ship is easing in from the edge of the system with the star
 * ahead of it — the part worth watching. Winding down at the same rate it wound
 * up meant watching it through a haze that had no reason to still be there.
 */
const CROSSING_OUT = 1.0;

/**
 * The gas, which mounts *below* the flight canvas.
 *
 * Gas behind, ship in front: you are flying through the cloud rather than
 * looking at a picture of one. The flight renderer clears transparent and the
 * crossing shell erases the system down to this, so the hull draws over it while
 * the system it is leaving does not.
 *
 * Still its own renderer, and it has to be: the flight scene is destroyed
 * mid-crossing when the system is swapped, and this is what covers that. The
 * streaks had no such constraint and have moved into the scene proper, where
 * depth can sort them against the hull — see `warpStreaks`.
 */
export function WarpGas({ crossing }: { crossing: boolean }) {
  return (
    <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none' }}>
      <NebulaField crossing={crossing} />
    </div>
  );
}


// ─── Gas ─────────────────────────────────────────────────────────────────────

/**
 * How much of the screen's pixels the gas is drawn at.
 *
 * It is all soft gradients, so it survives being drawn small and stretched —
 * and the noise below costs enough per pixel to make that the difference
 * between free and noticeable.
 */
const RESOLUTION_SCALE = 0.6;

const vertexShader = /* glsl */ `
  varying vec2 vUv;
  void main() {
    vUv = uv;
    gl_Position = vec4(position.xy, 0.0, 1.0);
  }
`;

const fragmentShader = /* glsl */ `
  precision highp float;

  uniform float uTime;
  /** 0..1. The gas only exists during a crossing. */
  uniform float uCrossing;
  uniform float uAspect;

  varying vec2 vUv;

  /*
   * ── Periodic in x, which is what closes the seam ────────────────────────
   *
   * The angle around the screen is a circle: -PI and +PI are the same
   * direction. A noise lattice does not know that, so the field either side of
   * that line was uncorrelated and the join showed as a hard radial edge.
   *
   * Wrapping the lattice coordinate makes the field genuinely periodic — the
   * cell at x = period is the cell at x = 0, so the two sides of the join are
   * not merely similar, they are the same samples.
   *
   * It only works if the period stays a whole number of cells through every
   * octave, so the fbm below doubles the period exactly as it doubles the frequency —
   * and the octave scale is 2.0 rather than the 2.03 usually used to break up
   * lattice alignment. That is the price of a seamless circle.
   */
  float hash(vec2 p, float period) {
    p.x = mod(p.x, period);
    return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453123);
  }

  float noise(vec2 p, float period) {
    vec2 i = floor(p);
    vec2 f = fract(p);
    vec2 u = f * f * (3.0 - 2.0 * f);

    return mix(
      mix(hash(i, period), hash(i + vec2(1.0, 0.0), period), u.x),
      mix(hash(i + vec2(0.0, 1.0), period), hash(i + vec2(1.0, 1.0), period), u.x),
      u.y
    );
  }

  /* Four octaves. A fifth is not visible through this much bloom and costs a
     quarter again per pixel. */
  float fbm(vec2 p, float period) {
    float total = 0.0;
    float amplitude = 0.5;

    for (int i = 0; i < 4; i++) {
      total += amplitude * noise(p, period);
      p *= 2.0;
      period *= 2.0;
      amplitude *= 0.5;
    }
    return total;
  }

  void main() {
    vec2 uv = (vUv - 0.5) * vec2(uAspect, 1.0);
    float radius = length(uv);
    float angle = atan(uv.y, uv.x);

    /*
     * Log-polar, which is what makes this read as motion rather than as a
     * spinning disc.
     *
     * Scrolling a noise field along log(radius) means a feature moves outward
     * *and* grows as it goes, which is what passing something actually does.
     */
    float depth = log(max(radius, 0.0025));

    // Turns rather than radians, so an angular frequency is a whole number of
    // lattice cells around the circle and the wrap above lines up.
    float turns = angle / 6.2831853 + 0.5;
    float along = depth * 1.25 - uTime * 0.42;

    float gas = fbm(vec2(turns * 3.0, along * 0.95), 3.0);
    gas = pow(max(gas, 0.0), 2.1) * 3.4;

    /*
     * Colour from three independent fields rather than one.
     *
     * A single field can only ever slide along one axis of a gradient, which
     * gives a cloud that is violet on one side and blue on the other and no
     * more. Three uncorrelated fields put different hues in different places
     * and let them meet, so the cloud has veins of one colour running through
     * banks of another — which is what a real emission nebula looks like and
     * what "more colourful" actually means.
     */
    float fieldA = fbm(vec2(turns * 2.0, along * 0.62 + 17.0), 2.0);
    float fieldB = fbm(vec2(turns * 2.0, along * 0.83 + 4.0), 2.0);
    float fieldC = fbm(vec2(turns, along * 0.47 + 91.0), 1.0);

    vec3 tint = mix(vec3(0.55, 0.13, 1.00), vec3(0.10, 0.78, 1.00), smoothstep(0.3, 0.75, fieldB));
    tint = mix(tint, vec3(1.00, 0.16, 0.62), smoothstep(0.42, 0.80, fieldA));
    tint = mix(tint, vec3(0.20, 1.00, 0.72), smoothstep(0.55, 0.88, fieldC) * 0.75);

    // Pushed away from grey: the fbm sum sits around a half, and colour mixed
    // at that level washes out to something near white before it is bright.
    tint = normalize(tint + 0.001) * 1.35;

    // Thinner towards the middle, which is where you are going and should stay
    // the clearest part of the screen.
    gas *= smoothstep(0.02, 0.44, radius);

    vec3 colour = tint * gas * uCrossing;

    /*
     * The veil, and what actually hides the swap.
     *
     * Radial: the middle stays comparatively clear because it is where you are
     * heading, and the edges wash out first. It is also why an arrival reads as
     * coming *out* of something rather than as a picture being uncovered.
     */
    float veil = uCrossing * mix(0.55, 1.0, smoothstep(0.0, 0.75, radius));
    float lit = min(1.0, colour.r + colour.g + colour.b);

    gl_FragColor = vec4(colour, max(veil, lit));
  }
`;

function NebulaField({ crossing }: { crossing: boolean }) {
  const mountRef = useRef<HTMLDivElement>(null);
  const wanted = useRef(crossing);
  wanted.current = crossing;

  useEffect(() => {
    const mount = mountRef.current;
    if (!mount) return;

    const renderer = new THREE.WebGLRenderer({ alpha: true, antialias: false });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2) * RESOLUTION_SCALE);
    renderer.setClearColor(0x000000, 0);
    mount.appendChild(renderer.domElement);

    renderer.domElement.style.width = '100%';
    renderer.domElement.style.height = '100%';

    const scene = new THREE.Scene();
    const camera = new THREE.Camera();

    const uniforms = {
      uTime: { value: 0 },
      uCrossing: { value: 0 },
      uAspect: { value: 1 },
    };

    const quad = new THREE.Mesh(
      new THREE.PlaneGeometry(2, 2),
      new THREE.ShaderMaterial({
        uniforms,
        vertexShader,
        fragmentShader,
        transparent: true,
        depthTest: false,
        depthWrite: false,
      }),
    );
    scene.add(quad);

    const resize = () => {
      const width = mount.clientWidth;
      const height = mount.clientHeight;
      if (width === 0 || height === 0) return;

      renderer.setSize(width, height, false);
      uniforms.uAspect.value = width / height;
    };
    resize();
    window.addEventListener('resize', resize);

    let fade = 0;
    let frame = 0;
    let last = performance.now();

    const tick = (now: number) => {
      frame = requestAnimationFrame(tick);

      const step = Math.min(0.05, (now - last) / 1000);
      last = now;

      const target = wanted.current ? 1 : 0;
      const rate = target > fade ? 1 / CROSSING_IN : 1 / CROSSING_OUT;
      fade += Math.sign(target - fade) * Math.min(Math.abs(target - fade), rate * step);

      // Not crossing, nothing to draw. Skipping the draw entirely is what keeps
      // this free the overwhelming majority of the time.
      if (fade <= 0.002) {
        if (renderer.domElement.style.visibility !== 'hidden') {
          renderer.domElement.style.visibility = 'hidden';
        }
        return;
      }
      if (renderer.domElement.style.visibility === 'hidden') {
        renderer.domElement.style.visibility = '';
      }

      uniforms.uTime.value = now / 1000;
      uniforms.uCrossing.value = fade;

      renderer.render(scene, camera);
    };
    frame = requestAnimationFrame(tick);

    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener('resize', resize);

      quad.geometry.dispose();
      (quad.material as THREE.Material).dispose();
      renderer.dispose();
      mount.removeChild(renderer.domElement);
    };
  }, []);

  return <div ref={mountRef} style={{ position: 'absolute', inset: 0 }} />;
}
